import { useEffect, useRef, useState, type FormEvent } from 'react';
import { CHAT_MAX_LENGTH, type ChatScope } from '@etown/shared';
import { api } from '../../net/api';
import { useChatStore } from '../../stores/chatStore';
import { selectDmUnread, useDmStore } from '../../stores/dmStore';
import { useChatUnread } from './useChatUnread';
import { useOffice } from '../office/OfficeContext';
import { DmPanel } from './DmPanel';
import './chat.css';

type Tab = ChatScope | 'dm';

const TAB_LABELS: Record<Tab, string> = {
  global: 'Alle',
  proximity: 'In der Nähe',
  dm: 'Direkt',
};

/** Chat-Panel unten links: globaler Kanal, Naehe-Kanal und Direktnachrichten. */
export function ChatOverlay() {
  const { socket, mapId } = useOffice();
  const { messages, activeScope, isOpen, hasMore, setScope, setOpen, prependHistory } = useChatStore();
  const dmUnread = useDmStore(selectDmUnread);
  const totalUnread = useChatUnread();
  const [tab, setTab] = useState<Tab>('global');
  const [draft, setDraft] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Klick auf einen Nutzer in der Sidebar oeffnet direkt den DM-Tab
  const activePeerId = useDmStore((s) => s.activePeerId);
  useEffect(() => {
    if (activePeerId && isOpen) setTab('dm');
  }, [activePeerId, isOpen]);

  // Enter oeffnet den Chat, wenn gerade nicht getippt wird
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const el = document.activeElement;
      const typing = el?.tagName === 'INPUT' || el?.tagName === 'TEXTAREA';
      if (event.key === 'Enter' && !typing && !isOpen) {
        setOpen(true);
        requestAnimationFrame(() => inputRef.current?.focus());
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, setOpen]);

  // Bei neuen Nachrichten ans Ende scrollen
  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages, isOpen, tab]);

  const send = (event: FormEvent): void => {
    event.preventDefault();
    const body = draft.trim();
    if (!body || tab === 'dm') return;
    setSendError(null);
    socket.emit('chat:send', { scope: tab, body }, (res) => {
      if (res.ok) {
        useChatStore.getState().addMessage(res.data);
        setDraft('');
      } else {
        setSendError(res.error);
      }
    });
  };

  const loadOlder = async (): Promise<void> => {
    const oldest = messages.find((m) => m.scope === 'global');
    const page = await api.chatHistory(mapId, oldest?.id);
    prependHistory(page.messages, page.hasMore);
  };

  const selectTab = (next: Tab): void => {
    setTab(next);
    if (next === 'global' || next === 'proximity') setScope(next);
  };

  if (!isOpen) {
    return (
      <button className="chat-fab" onClick={() => setOpen(true)} aria-label="Chat öffnen">
        💬
        {totalUnread > 0 && <span className="chat-fab__badge">{totalUnread}</span>}
      </button>
    );
  }

  const visible = tab === 'dm' ? [] : messages.filter((m) => m.scope === activeScope);

  return (
    <section className="chat" aria-label="Chat">
      <header className="chat__header">
        <div className="chat__tabs" role="tablist">
          {(['global', 'proximity', 'dm'] as const).map((t) => (
            <button
              key={t}
              role="tab"
              aria-selected={tab === t}
              className={`chat__tab ${tab === t ? 'chat__tab--active' : ''}`}
              onClick={() => selectTab(t)}
            >
              {TAB_LABELS[t]}
              {t === 'dm' && dmUnread > 0 && <span className="chat__tab-badge">{dmUnread}</span>}
            </button>
          ))}
        </div>
        <button className="chat__close" onClick={() => setOpen(false)} aria-label="Chat schließen">
          ✕
        </button>
      </header>

      {tab === 'dm' ? (
        <DmPanel />
      ) : (
        <>
          <div ref={listRef} className="chat__list">
            {tab === 'global' && hasMore && (
              <button className="chat__more" onClick={() => void loadOlder()}>
                Aeltere Nachrichten laden
              </button>
            )}
            {visible.length === 0 && <p className="chat__empty">Noch keine Nachrichten.</p>}
            {visible.map((message) => (
              <div key={`${message.scope}-${message.id}`} className="chat__message">
                <span className="chat__author">{message.displayName}</span>
                <span className="chat__time">
                  {new Date(message.createdAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                </span>
                <p className="chat__body">{message.body}</p>
              </div>
            ))}
          </div>

          {sendError && <p className="chat__error" role="alert">{sendError}</p>}
          <form className="chat__form" onSubmit={send}>
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              maxLength={CHAT_MAX_LENGTH}
              placeholder={tab === 'global' ? 'Nachricht an alle…' : 'Nachricht an Personen in der Nähe…'}
            />
            <button type="submit" disabled={!draft.trim()}>
              Senden
            </button>
          </form>
        </>
      )}
    </section>
  );
}
